import React, { useState, useEffect, useMemo, useRef } from 'react';

import styled from 'styled-components';
import { parseLRC } from '../utils/lrcParser';

const LyricsContainer = styled.div`
    margin: 0px auto;
    width: ${props => props.maxWidth};
    max-width: ${props => props.maxWidth};
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    cursor: ${props => props.isExpanded ? 'default' : 'pointer'};
`;

const LyricsButton = styled.div`
    max-width: ${props => props.maxWidth};
    padding: ${props => props.isExpanded ? '0' : '20px 50px'};
    background: ${props => props.isExpanded ? 'transparent' : `
        /* 横线效果 */
        repeating-linear-gradient(
            transparent,
            transparent 28px,
            rgba(0, 0, 0, 0.08) 28px,
            rgba(0, 0, 0, 0.08) 30px
        ),
        /* 纸张背景 */
        linear-gradient(
            to bottom,
            #fefefe 0%,
            #fafafa 50%,
            #f5f5f5 100%
        )
    `};
    /* 展开时移除所有背景，因为底下已经有纸张背景了 */
    border: none;
    border-radius: 2px;
    color: #333;
    font-size: ${props => {
        if (props.fontSize) {
            const baseSize = parseFloat(props.fontSize);
            return props.isExpanded ? `${baseSize * 1.08}rem` : `${baseSize}rem`;
        }
        return props.isExpanded ? '1.4rem' : '1.3rem';
    }};
    font-weight: ${props => props.isExpanded ? '600' : '500'};
    font-family: 'Courier New', 'Georgia', serif;
    transition: all 0.4s ease;
    display: flex;
    align-items: center;
    justify-content: ${props => props.isExpanded ? 'space-between' : 'center'};
    gap: 12px;
    box-shadow: ${props => props.isExpanded ? 'none' : `
        0 2px 8px rgba(0, 0, 0, 0.15),
        0 0 0 1px rgba(0, 0, 0, 0.05),
        inset 0 1px 0 rgba(255, 255, 255, 0.8)
    `};
    position: absolute;
    top: 20px;
    text-align: ${props => props.isExpanded ? 'left' : 'center'};
    transform-style: preserve-3d;
    margin-bottom: ${props => props.isExpanded ? '25px' : '0'};
    padding-bottom: ${props => props.isExpanded ? '15px' : '0'};
    border-bottom: ${props => props.isExpanded ? '2px solid rgba(0, 0, 0, 0.1)' : 'none'};

    /* 纸张左侧的装订线效果 - 只在未展开时显示 */
    &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: ${props => props.isExpanded ? '0' : '30px'};
        background: ${props => props.isExpanded ? 'transparent' : `
            repeating-linear-gradient(
                transparent,
                transparent 28px,
                rgba(255, 0, 0, 0.15) 28px,
                rgba(255, 0, 0, 0.15) 30px
            ),
            linear-gradient(
                to right,
                rgba(255, 0, 0, 0.1) 0%,
                transparent 100%
            )
        `};
        border-right: ${props => props.isExpanded ? 'none' : '1px solid rgba(255, 0, 0, 0.2)'};
        transition: all 0.4s ease;
    }

    &:hover {
        transform: ${props => props.isExpanded ? 'none' : 'translateY(-3px) rotateX(2deg)'};
        box-shadow: ${props => props.isExpanded ? 'none' : `
            0 8px 20px rgba(0, 0, 0, 0.2),
            0 0 0 1px rgba(0, 0, 0, 0.05),
            inset 0 1px 0 rgba(255, 255, 255, 0.8)
        `};
    }

    &:active {
        transform: ${props => props.isExpanded ? 'none' : 'translateY(-1px) rotateX(1deg)'};
    }

    /* 箭头图标 */
    &::after {
        content: '${props => props.isExpanded ? '▼' : '▶'}';
        font-size: 0.9rem;
        color: #666;
        transition: transform 0.4s ease;
        transform: ${props => props.isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
        margin-left: ${props => props.isExpanded ? '0' : '8px'};
    }
`;

const LyricsSection = styled.div`
    left: 0px;
    width: ${props => props.maxWidth};
    max-width: ${props => props.maxWidth};
    padding: ${props => props.isExpanded ? '100px 40px 60px' : '0'};
    background: ${props => props.isExpanded ? `
        /* 横线效果 */
        repeating-linear-gradient(
            transparent,
            transparent 28px,
            rgba(0, 0, 0, 0.06) 28px,
            rgba(0, 0, 0, 0.06) 30px
        ),
        /* 纸张背景 */
        linear-gradient(
            to bottom,
            #fefefe 0%,
            #fafafa 50%,
            #f5f5f5 100%
        )
    ` : 'transparent'};
    border-radius: 4px;
    border: ${props => props.isExpanded ? '1px solid rgba(0, 0, 0, 0.1)' : 'none'};
    box-shadow: ${props => props.isExpanded ? `
        0 4px 12px rgba(0, 0, 0, 0.15),
        0 0 0 1px rgba(0, 0, 0, 0.05),
        inset 0 1px 0 rgba(255, 255, 255, 0.8)
    ` : 'none'};
    max-height: ${props => props.isExpanded ? '600px' : '0'};
    overflow: ${props => props.isExpanded ? 'visible' : 'hidden'};
    transition: all 0.4s ease;
    opacity: ${props => props.isExpanded ? '1' : '0'};
    position: relative;
    display: flex;
    flex-direction: column;

    /* 左侧装订线 */
    &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 35px;
        background: ${props => props.isExpanded ? `
            repeating-linear-gradient(
                transparent,
                transparent 28px,
                rgba(255, 0, 0, 0.12) 28px,
                rgba(255, 0, 0, 0.12) 30px
            ),
            linear-gradient(
                to right,
                rgba(255, 0, 0, 0.08) 0%,
                transparent 100%
            )
        ` : 'transparent'};
        border-right: ${props => props.isExpanded ? '1px solid rgba(255, 0, 0, 0.15)' : 'none'};
        transition: all 0.4s ease;
    }

    /* 右上角装饰 - 笔图标区域 */
    &::after {
        content: '✎';
        position: absolute;
        top: 15px;
        right: 20px;
        font-size: 1.5rem;
        color: rgba(0, 0, 0, 0.3);
        opacity: ${props => props.isExpanded ? '1' : '0'};
        transition: opacity 0.4s ease;
        transform: rotate(-15deg);
    }
`;

const LyricsContent = styled.div`
    width: ${props => props.maxWidth};
    max-width: ${props => props.maxWidth};
    max-height: ${props => props.isExpanded ? '440px' : '0'};
    overflow-y: auto;
    overflow-x: hidden;
    padding: 10px 0;
    line-height: 2.2;
    font-size: ${props => props.fontSize || '1rem'};
    text-align: left;
    color: #333;
    font-family: 'Georgia', 'Times New Roman', serif;
    position: relative;
    flex: 1;

    /* 对齐横线 */
    padding-left: 5px;

    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.05);
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.2);
        border-radius: 4px;

        &:hover {
            background: rgba(0, 0, 0, 0.3);
        }
    }
`;

const LyricLine = styled.div`
    margin: 0;
    padding: 2px 0;
    min-height: 28px;
    opacity: ${props => props.isActive ? 1 : 0.7};
    font-weight: ${props => props.isActive ? 600 : 400};
    transition: all 0.3s ease;
    color: ${props => props.isActive ? '#1a1a1a' : '#444'};
    position: relative;
    padding-left: ${props => props.isActive ? '8px' : '0'};
    border-left: ${props => props.isActive ? `3px solid ${props.highlightColor || 'rgba(244, 212, 163, 0.6)'}` : 'none'};
    background: ${props => {
        if (!props.isActive) return 'transparent';
        // 將高亮顏色的透明度改為 0.1
        const color = props.highlightColor || 'rgba(244, 212, 163, 0.6)';
        return color.replace(/[\d.]+\)$/g, '0.1)');
    }};
    margin-left: ${props => props.isActive ? '-8px' : '0'};
`;

const LockButton = styled.button`
    position: absolute;
    top: 15px;
    right: 0px;
    background: ${props => props.isLocked ? 'rgba(244, 212, 163, 0.8)' : 'rgba(0, 0, 0, 0.1)'};
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    padding: 6px 12px;
    font-size: 0.85rem;
    color: ${props => props.isLocked ? '#1a1a1a' : '#666'};
    cursor: pointer;
    transition: all 0.3s ease;
    opacity: ${props => props.isExpanded ? '1' : '0'};
    pointer-events: ${props => props.isExpanded ? 'auto' : 'none'};
    z-index: 10;
    font-family: 'Courier New', 'Georgia', serif;
    
    &:hover {
        background: ${props => props.isLocked ? 'rgba(244, 212, 163, 1)' : 'rgba(0, 0, 0, 0.15)'};
        transform: scale(1.05);
    }
    
    &:active {
        transform: scale(0.95);
    }
`;

/**
 * LRCLyricViewer - 動態歌詞顯示組件（支持 LRC 格式）
 * 
 * @param {string} lrcPath - LRC 文件路徑（相對於 public 目錄，如 '/data/lyrics/song.lrc'）
 * @param {number} currentTime - 當前播放時間（秒），用於自動高亮對應歌詞
 * @param {string} title - 標題文字（可選，如果提供 LRC 文件，會從文件中讀取標題）
 * @param {boolean} defaultExpanded - 是否默認展開（可選，默認為 false）
 * @param {string} highlightColor - 高亮顏色（可選，默認為 rgba(244, 212, 163, 0.6)）
 * @param {string} maxWidth - 最大寬度（可選，默認為 '314px'）
 * @param {string} fontSize - 字體大小（可選，默認為 '1rem'，會應用到歌詞內容，標題會根據展開狀態自動調整）
 * 
 * 兼容舊 API：
 * @param {Array<string>} lyrics - 歌詞數組（舊 API，如果提供了 lrcPath 則忽略此參數）
 * @param {number} currentLyricIndex - 當前播放的歌詞索引（舊 API，如果提供了 currentTime 則忽略此參數）
 */
const LRCLyricViewer = ({ 
    maxWidth = '314px',
    lrcPath = null,
    currentTime = -1,
    title = null,
    defaultExpanded = false,
    highlightColor = 'rgba(244, 212, 163, 0.6)',
    fontSize = '1rem',
    // 兼容舊 API
    lyrics: legacyLyrics = [],
    currentLyricIndex: legacyCurrentLyricIndex = -1
}) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const [lrcContent, setLrcContent] = useState(null);
    const [lrcTitle, setLrcTitle] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [autoScroll, setAutoScroll] = useState(false);
    // 緩存上一次的索引，用於優化查找性能
    const lastIndexRef = useRef(-1);
    // 引用歌词容器和歌词行
    const lyricsContentRef = useRef(null);
    const lyricLineRefs = useRef({});
    // 用于区分程序滚动和用户滚动
    const isProgrammaticScrollRef = useRef(false);

    // 加載 LRC 文件
    useEffect(() => {
        if (!lrcPath) {
            setLrcContent(null);
            setLrcTitle(null);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);
        
        fetch(lrcPath)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`[LRCLyricView] Failed to load LRC file: ${response.statusText}`);
                }
                return response.text();
            })
            .then(text => {
                const { lyrics, title: parsedTitle } = parseLRC(text);
                setLrcContent(lyrics);
                setLrcTitle(parsedTitle);
                // 重置緩存的索引和 refs
                lastIndexRef.current = -1;
                lyricLineRefs.current = {};
                setLoading(false);
            })
            .catch(err => {
                console.error('[LRCLyricView] Error loading LRC file:', err);
                setError(err.message);
                setLoading(false);
            });
    }, [lrcPath]);

    // 解析歌詞數據
    const lyricsData = useMemo(() => {
        // 如果提供了 LRC 文件，使用 LRC 數據
        if (lrcContent && lrcContent.length > 0) {
            return lrcContent.map(item => item.text);
        }
        // 否則使用舊的 lyrics 數組
        return legacyLyrics;
    }, [lrcContent, legacyLyrics]);

    // 優化的查找函數：從上次的索引開始查找
    const findCurrentLyricIndexOptimized = (lyrics, currentTime, startIndex) => {
        if (!lyrics || lyrics.length === 0) return -1;
        
        // 如果 startIndex 無效，從頭開始
        if (startIndex < 0 || startIndex >= lyrics.length) {
            startIndex = 0;
        }
        
        let index = -1;
        
        // 如果當前時間大於等於上次索引的時間，向後查找（正常播放）
        if (currentTime >= lyrics[startIndex].time) {
            // 向後查找：找到最後一個時間小於等於當前時間的歌词
            index = startIndex;
            for (let i = startIndex; i < lyrics.length; i++) {
                if (lyrics[i].time <= currentTime) {
                    index = i;
                } else {
                    break;
                }
            }
        } else {
            // 向前查找：時間倒退了（可能是用戶拖動了進度條）
            // 從 startIndex 向前查找，找到最後一個時間小於等於當前時間的歌词
            for (let i = startIndex; i >= 0; i--) {
                if (lyrics[i].time <= currentTime) {
                    index = i;
                    break;
                }
            }
        }
        
        return index;
    };

    // 計算當前高亮的歌詞索引
    const currentLyricIndex = useMemo(() => {
        // 如果提供了 currentTime 和 LRC 數據，根據時間計算
        if (currentTime >= 0 && lrcContent && lrcContent.length > 0) {
            const index = findCurrentLyricIndexOptimized(
                lrcContent, 
                currentTime, 
                lastIndexRef.current
            );
            // 更新緩存的索引
            lastIndexRef.current = index;
            return index;
        }
        // 否則使用舊的 currentLyricIndex
        return legacyCurrentLyricIndex;
    }, [currentTime, lrcContent, legacyCurrentLyricIndex]);

    // 自動滾動到當前歌詞
    useEffect(() => {
        if (!autoScroll || !isExpanded || currentLyricIndex < 0 || !lyricsContentRef.current) {
            return;
        }

        const lineElement = lyricLineRefs.current[currentLyricIndex];
        if (!lineElement) {
            return;
        }

        const container = lyricsContentRef.current;
        
        // 計算當前歌詞相對於容器的位置
        const lineRect = lineElement.getBoundingClientRect();
        
        // 計算相對位置（考慮當前滾動位置）
        const lineOffsetTop = lineElement.offsetTop;
        const containerHeight = container.clientHeight;
        const lineHeight = lineRect.height;
        
        // 計算目標滾動位置，讓當前歌詞居中顯示
        const targetScrollTop = lineOffsetTop - (containerHeight / 2) + (lineHeight / 2);

        // 標記為程序滾動
        isProgrammaticScrollRef.current = true;
        container.scrollTo({
            top: Math.max(0, targetScrollTop),
            behavior: 'smooth'
        });

        // 重置標記（使用 setTimeout 確保滾動事件已經觸發）
        setTimeout(() => {
            isProgrammaticScrollRef.current = false;
        }, 500);
    }, [currentLyricIndex, autoScroll, isExpanded]);

    // 監聽用戶滾動事件
    useEffect(() => {
        const container = lyricsContentRef.current;
        if (!container || !isExpanded) {
            return;
        }

        const handleScroll = () => {
            // 如果是程序滾動，不處理
            if (isProgrammaticScrollRef.current) {
                return;
            }

            // 用戶手動滾動，關閉自動滾動
            if (autoScroll) {
                setAutoScroll(false);
            }
        };

        container.addEventListener('scroll', handleScroll);
        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [autoScroll, isExpanded]);

    // 確定顯示的標題
    const displayTitle = title || lrcTitle || 'Lyrics';

    return (
        <LyricsContainer isExpanded={isExpanded} maxWidth={maxWidth}>
            <LyricsSection isExpanded={isExpanded} maxWidth={maxWidth}>
                <LyricsContent 
                    ref={lyricsContentRef}
                    maxWidth={maxWidth} 
                    fontSize={fontSize} 
                    isExpanded={isExpanded}
                >
                    {loading && <div>載入中...</div>}
                    {error && <div style={{ color: 'red' }}>錯誤: {error}</div>}
                    {!loading && !error && lyricsData.length === 0 && (
                        <div>沒有歌詞</div>
                    )}
                    {!loading && !error && lyricsData.map((lyric, index) => (
                        <LyricLine 
                            key={index}
                            ref={el => {
                                if (el) {
                                    lyricLineRefs.current[index] = el;
                                } else {
                                    delete lyricLineRefs.current[index];
                                }
                            }}
                            isActive={index === currentLyricIndex}
                            highlightColor={highlightColor}
                        >
                            {lyric}
                        </LyricLine>
                    ))}
                </LyricsContent>
            </LyricsSection>
            <LyricsButton 
                isExpanded={isExpanded}
                maxWidth={maxWidth}
                fontSize={fontSize}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                {displayTitle}
            </LyricsButton>
            <LockButton
                isExpanded={isExpanded}
                isLocked={autoScroll}
                onClick={(e) => {
                    e.stopPropagation();
                    setAutoScroll(!autoScroll);
                }}
                title={autoScroll ? '解除鎖定' : '鎖定當前歌詞'}
            >
                {autoScroll ? '🔒' : '🔓'}
            </LockButton>
        </LyricsContainer>
    );
};

export default LRCLyricViewer;
